import {
  productFromFirestore,
  ProductStatus,
  productVersionFromFirestore,
} from '@parallel-fluidics/shared/models/products';
import * as Sentry from '@sentry/react';
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  where,
} from 'firebase/firestore';

/**
 * Find the catalog product + version that matches a traveler's (projectId, pPartId),
 * searching across ALL version statuses (active, retired, draft) via collectionGroup.
 *
 * Mirrors the cloud function's findProductVersionByPart (productLookup.js) so the
 * frontend makes the same binding decision as the backend, including its
 * `{ product, version }` return shape.
 *
 * Returns null when no match is found or the matched docs fail validation.
 * Requires collectionGroup indexes on versions: (projectId, pPartId) and
 * (projectId, pPartId, status) — see apps/functions/firestore.indexes.json.
 *
 * @param {string} projectId  Traveler's project field (e.g. "P001")
 * @param {string} pPartId    Traveler's pPart field
 * @returns {Promise<{
 *   product: import('@parallel-fluidics/shared/models/products').Product,
 *   version: import('@parallel-fluidics/shared/models/products').ProductVersion
 * } | null>}
 */
export default async function findProductByPart(projectId, pPartId) {
  if (!projectId || !pPartId) return null;

  const db = getFirestore();
  const versionsQuery = query(
    collectionGroup(db, 'versions'),
    where('projectId', '==', projectId),
    where('pPartId', '==', pPartId),
  );

  // Query for the ACTIVE version explicitly: a plain limited query could miss
  // it once a part accumulates more versions than the limit.
  let snap = await getDocs(
    query(versionsQuery, where('status', '==', ProductStatus.ACTIVE), limit(2)),
  );

  if (snap.empty) {
    // No active version — fall back to any version for this part.
    snap = await getDocs(query(versionsQuery, limit(2)));
  }

  if (snap.empty) return null;

  const versionDoc = snap.docs[0];

  if (snap.size > 1) {
    Sentry.captureMessage('Multiple product versions match project and part mapping', {
      level: 'warning',
      extra: {
        projectId,
        pPartId,
        selectedVersionId: versionDoc.id,
        matchCount: snap.size,
      },
    });
  }
  const productRef = versionDoc.ref.parent.parent;
  const productSnap = await getDoc(doc(db, 'products', productRef.id));
  if (!productSnap.exists()) return null;

  const product = productFromFirestore(productSnap);
  const version = productVersionFromFirestore(versionDoc);

  if (product.validation === 'failed' || version.validation === 'failed' || !product.sku) {
    Sentry.captureMessage('Product/version mapping is invalid', {
      level: 'error',
      extra: {
        projectId,
        pPartId,
        productDocId: productSnap.id,
        productVersionId: versionDoc.id,
        productValidation: product.validation,
        versionValidation: version.validation,
      },
    });
    return null;
  }

  return { product, version };
}
